import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import useProductName from "brand-customization/useProductName";
import Routes from "routing/Routes";
import EnterpriseRoutes from "../EnterpriseRoutes";
const CHECKS = {
  server: {},
  "server.node": { entityListUrl: Routes.SYSTEM.CLUSTER.NODES },
  "server.input": { entityListUrl: Routes.SYSTEM.INPUTS },
  "server.processing": { entityListUrl: Routes.SYSTEM.CLUSTER.NODES },
  "server.output": {},
  "server.archiving": { entityListUrl: EnterpriseRoutes.ARCHIVES },
  "server.data_lake": { entityListUrl: EnterpriseRoutes.DATA_LAKE },
  "server.integrations": {},
  search_cluster: {},
  "search_cluster.server": { entityListUrl: Routes.SYSTEM.CLUSTER.NODES },
  "search_cluster.index_management": { entityListUrl: Routes.SYSTEM.INDICES.LIST },
  mongodb: { entityListUrl: Routes.SYSTEM.CLUSTER.NODES },
  forwarders: { meaning: true, commonCauses: 3, recommendedAction: true, entityListUrl: EnterpriseRoutes.FORWARDERS },
  collectors: {
    meaning: true,
    commonCauses: 3,
    recommendedAction: true,
    entityListUrl: Routes.SYSTEM.SIDECARS.OVERVIEW
  },
  "server.node.storage": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.node.cpu": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.node.memory": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.node.certificates": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.node.processing_state": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.node.load_balancer": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.input.input_buffer": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.input.input_failures": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.processing.processing_buffer": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.processing.journal_size": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.processing.node_lag": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.output.output_buffer": {
    meaning: true,
    commonCauses: 3,
    recommendedAction: true,
    entityListUrl: Routes.SYSTEM.CLUSTER.NODES
  },
  "server.output.report_generation": {
    meaning: true,
    commonCauses: 3,
    recommendedAction: true,
    entityListUrl: EnterpriseRoutes.REPORTS
  },
  "server.archiving.archive_failures": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.data_lake.connectivity": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.data_lake.message_drops": { meaning: true, commonCauses: 3, recommendedAction: true },
  "server.integrations.idp_sync": {
    meaning: true,
    commonCauses: 3,
    recommendedAction: true,
    entityListUrl: Routes.SYSTEM.AUTHENTICATION.BACKENDS.OVERVIEW
  },
  "server.integrations.email_transport": {
    meaning: true,
    commonCauses: 3,
    recommendedAction: true,
    entityListUrl: Routes.SYSTEM.CONFIGURATIONS
  },
  "search_cluster.server.storage": { meaning: true, commonCauses: 3, recommendedAction: true },
  "search_cluster.server.cpu": { meaning: true, commonCauses: 3, recommendedAction: true },
  "search_cluster.server.memory": { meaning: true, commonCauses: 3, recommendedAction: true },
  "search_cluster.server.certificates": { meaning: true, commonCauses: 3, recommendedAction: true },
  "search_cluster.server.state": { meaning: true, commonCauses: 4, recommendedAction: true },
  "search_cluster.server.quorum": { meaning: true, commonCauses: 2, recommendedAction: true },
  "search_cluster.index_management.rotation": { meaning: true, commonCauses: 3, recommendedAction: true },
  "search_cluster.index_management.retention_delete": { meaning: true, commonCauses: 3, recommendedAction: true },
  "search_cluster.index_management.warm_tier_move": { meaning: true, commonCauses: 2, recommendedAction: true },
  "search_cluster.index_management.shard_count": { meaning: true, commonCauses: 3, recommendedAction: true },
  "mongodb.connectivity": { meaning: true, commonCauses: 3, recommendedAction: true },
  "mongodb.primary_state": { meaning: true, commonCauses: 3, recommendedAction: true },
  "mongodb.slow_queries": { meaning: true, commonCauses: 3, recommendedAction: true },
  "mongodb.storage": { meaning: true, commonCauses: 2, recommendedAction: true }
};
const buildHealthCheckDefinitions = (t, productName) => Object.fromEntries(
  Object.entries(CHECKS).map(([id, texts]) => {
    const text = (key) => t(`checks.${id}.${key}`, { productName });
    return [
      id,
      {
        description: text("description"),
        meaning: texts.meaning ? text("meaning") : void 0,
        commonCauses: texts.commonCauses ? Array.from({ length: texts.commonCauses }, (_, index) => text(`common_causes.${index + 1}`)) : void 0,
        recommendedAction: texts.recommendedAction ? text("recommended_action") : void 0,
        entityList: texts.entityListUrl ? { url: texts.entityListUrl, label: text("entity_list") } : void 0
      }
    ];
  })
);
export const useHealthCheckDefinitions = () => {
  const { t } = useTranslation("enterprise_health");
  const productName = useProductName();
  return useMemo(() => buildHealthCheckDefinitions(t, productName), [t, productName]);
};
export const getEntityListFor = (definitions, id) => {
  var _a;
  const own = (_a = definitions[id]) == null ? void 0 : _a.entityList;
  if (own) return own;
  const lastDot = id.lastIndexOf(".");
  return lastDot === -1 ? void 0 : getEntityListFor(definitions, id.slice(0, lastDot));
};
const UNIT_NOUN_IDS = /* @__PURE__ */ new Set([
  "server.node",
  "server.input",
  "server.processing",
  "server.output.output_buffer",
  "server.output.report_generation",
  "server.archiving",
  "server.data_lake",
  "server.integrations",
  "search_cluster.server.state",
  "search_cluster.server.quorum",
  "search_cluster.server",
  "search_cluster.index_management",
  "search_cluster.index_management.shard_count",
  "mongodb"
]);
export const getUnitNounFor = (id) => {
  if (UNIT_NOUN_IDS.has(id)) return i18n.t(`checks.${id}.unit`, { ns: "enterprise_health" });
  const lastDot = id.lastIndexOf(".");
  return lastDot === -1 ? void 0 : getUnitNounFor(id.slice(0, lastDot));
};
